import React, {useEffect, useMemo, useRef, useState} from 'react';

import {getBillingNoticeDefaults, previewBillingNotice, sendBillingNotice} from '../../api/system/system.api';

/**
 * 결제 예정 안내 문자(LMS) 패널(2026-10-02). 정산관리 화면의 '결제 안내 문자' 버튼으로 연다.
 *
 * ## 왜
 * 예고 없이 카드 결제가 나가 "이게 뭐냐" 문의·카드사 민원·빌링키 해제가 있었다. 결제 **전에**
 * 금액·결제일·결제 회사(헤드리스 주식회사)와 모바일 고지서 링크를 문자로 보낸다.
 *
 * ## 흐름
 * 0. 팝업을 열면 서버 기본 문구·제목이 바로 채워진다 — 미리보기 전에 고칠 수 있다(2026-10-02 사용자 요청).
 * 1. 결제 예정일·문의처·제목·문구를 정하고 **[미리보기]** — 서버가 대상(그 달 확정 정산서)마다 완성 문구를 만든다.
 *    (고지서 링크가 없으면 이때 만든다. 이미 있으면 같은 링크를 쓴다 — 앞서 보낸 문자의 링크가 죽지 않는다.)
 * 2. **[시험 발송]** 으로 관리자 번호에 먼저 받아 본다. 시험은 고객에게 가지 않고 중복 판정에도 안 잡힌다.
 * 3. 체크한 건을 **[선택 발송]** — 한 건씩 차례로 부른다(진행률·부분 실패가 그대로 보이게). 중지할 수 있다.
 *
 * 🔑 미리보기 뒤에 설정을 바꾸면 발송 버튼을 잠근다 — 화면에 보이는 문구와 실제 나가는 문구가 달라지면 안 된다.
 * 🔴 서버도 같은 판정을 한다(확정 여부·중복·야간·번호·문구 길이). 이 화면의 잠금은 실수 방지용 1층이다.
 *
 * 설계: `_ai-runs/2026-10-02_001_billing_payment_notice/001_design.md`
 */

const LS_TEMPLATE = 'omoplay.billingNotice.template';
const LS_SUBJECT = 'omoplay.billingNotice.subject';
const LS_TEST_PHONE = 'omoplay.billingNotice.testPhone';

const lsGet = (k) => { try { return window.localStorage.getItem(k) ?? ''; } catch { return ''; } };
const lsSet = (k, v) => { try { if (v) window.localStorage.setItem(k, v); else window.localStorage.removeItem(k); } catch { /* 저장 불가 = 편의 기능만 빠진다 */ } };

const won = (n) => (n ?? 0).toLocaleString('ko-KR');

/** 기본 결제 예정일 = 오늘부터 3일 뒤(주말이면 다음 월요일). 안내 후 문의를 받을 시간을 둔다. */
const defaultScheduledOn = () => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    if (d.getDay() === 6) d.setDate(d.getDate() + 2);
    if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const errorText = (e) => e?.message || '요청을 처리하지 못했습니다.';

/** SureM 기준 바이트(한글 2, ASCII 1). 서버 `BillingNoticeText.byteLength` 와 같은 규칙 — 입력 중 길이 안내용. */
const byteLength = (t) => [...String(t ?? '')].reduce((n, ch) => n + (ch.charCodeAt(0) < 0x80 ? 1 : 2), 0);
const SUBJECT_MAX_BYTES = 30;

/** 이미 안내했는가 — 서버 `BillingNoticeText.countsAsSent` 와 같은 판정(결과를 모르는 건도 보낸 것으로 본다). */
const noticedAlready = (row) => {
    const last = row.lastNotice;
    if (!last || last.kind !== 'NOTICE') return false;
    const c = last.resultCode;
    return c == null || c === 'A0000' || c === 'UNKNOWN' || c === 'CALL_FAILED' || String(c).startsWith('HTTP_5');
};

export default function BillingNoticePanel({ month, onClose }) {
    const [scheduledOn, setScheduledOn] = useState(defaultScheduledOn());
    /*
      문의처 = 브랜드별 카카오톡 채널 채팅 링크(2026-10-02). 🔑 브라우저에 기억하지 않는다 —
      기준값은 서버(system_config → 코드 기본값) 하나여야 관리자·브라우저마다 다른 링크가 나가지 않는다.
      여기서 고친 값은 **이번 발송에만** 쓴다.
    */
    const [contactOmoplay, setContactOmoplay] = useState('');
    const [contactDangoltalk, setContactDangoltalk] = useState('');
    const [template, setTemplate] = useState(lsGet(LS_TEMPLATE));
    const [subject, setSubject] = useState(lsGet(LS_SUBJECT));
    const [testPhone, setTestPhone] = useState(lsGet(LS_TEST_PHONE));
    /** 서버 기본값(문구·제목·문의처). 팝업을 열 때 읽어 비어 있는 칸을 채운다. */
    const [defaults, setDefaults] = useState(null);

    const [data, setData] = useState(null);
    /** 마지막 미리보기에 쓴 설정. 지금 설정과 다르면 발송을 잠근다. */
    const [previewKey, setPreviewKey] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [expanded, setExpanded] = useState(null);
    const [results, setResults] = useState({}); // settlementId → {ok, text}
    const [sending, setSending] = useState(null); // {done, total}
    const stopRef = useRef(false);
    const [hidePaid, setHidePaid] = useState(true);

    /*
      🔑 팝업을 열자마자 기본 문구·제목을 칸에 채운다. 종전에는 미리보기를 눌러야 기본 문구가 보여서
         "보내기 전에 고친다" 가 불가능했다. 브라우저에 저장해 둔(고쳐 쓴) 값이 있으면 그것을 둔다.
    */
    useEffect(() => {
        let canceled = false;
        getBillingNoticeDefaults()
            .then((resp) => {
                // 🔑 정산 API 는 CommonResponse({code, data}) 를 그대로 돌려준다 — 값은 `.data` 에 있다.
                const d = resp?.data;
                if (canceled || !d) return;
                setDefaults(d);
                setTemplate((t) => t || d.template || '');
                setSubject((v) => v || d.subject || '');
                setContactOmoplay(d.contactOmoplay || '');
                setContactDangoltalk(d.contactDangoltalk || '');
            })
            .catch((e) => !canceled && setError(errorText(e)));
        return () => { canceled = true; };
    }, []);

    const settingsKey = JSON.stringify([scheduledOn, contactOmoplay, contactDangoltalk, template, subject]);
    const subjectBytes = byteLength(subject);
    const stale = previewKey !== null && previewKey !== settingsKey;

    const body = () => ({
        scheduledOn,
        contactOmoplay: contactOmoplay || null,
        contactDangoltalk: contactDangoltalk || null,
        template: template || null,
        subject: subject || null,
    });

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const res = (await previewBillingNotice({ month, ...body() }))?.data;
            if (!res?.rows) throw new Error('미리보기 응답을 해석하지 못했습니다.');
            setData(res);
            setPreviewKey(settingsKey);
            setResults({});
            // 기본 선택 = 보낼 수 있고 아직 안 보낸 건.
            setSelected(new Set(res.rows.filter((r) => r.problems.length === 0 && !noticedAlready(r)).map((r) => r.settlementId)));
        } catch (e) {
            setError(errorText(e));
        } finally {
            setLoading(false);
        }
    };

    const rows = useMemo(
        () => (data?.rows ?? []).filter((r) => !(hidePaid && r.paidAt)),
        [data, hidePaid]
    );
    const selectable = (r) => r.problems.length === 0;
    const selectedRows = rows.filter((r) => selected.has(r.settlementId));
    const selectedTotal = selectedRows.reduce((s, r) => s + r.total, 0);
    const resendCount = selectedRows.filter(noticedAlready).length;

    const toggle = (id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const sendTest = async (row) => {
        // 입력은 다듬기만 하고 그대로 보낸다 — 판정(`01012345678`·`010-1234-5678`·`+82 10-1234-5678` 전부 허용)은
        // 서버 PhoneNumber.mobileDigits 한 곳에서 한다. 여기서 `+` 를 지우면 `+82 10-…` 이 `8210…` 이 되어 서버가 거절한다.
        const phone = testPhone.trim();
        if (phone.replace(/\D/g, '').length < 10) { setError('시험 발송 받을 휴대폰 번호를 입력하세요.'); return; }
        lsSet(LS_TEST_PHONE, testPhone);
        setError('');
        try {
            const r = (await sendBillingNotice({ settlementId: row.settlementId, ...body(), testPhone: phone }))?.data ?? {};
            setResults((p) => ({ ...p, [row.settlementId]: { ok: r.accepted, text: `시험 ${r.accepted ? '접수' : '실패'} → ${r.phoneMasked} (${r.resultCode ?? '코드 없음'}${r.resultMessage ? ` ${r.resultMessage}` : ''})` } }));
        } catch (e) {
            setResults((p) => ({ ...p, [row.settlementId]: { ok: false, text: `시험 실패: ${errorText(e)}` } }));
        }
    };

    const sendSelected = async () => {
        const targets = selectedRows;
        if (targets.length === 0) return;
        const msg = [
            `${month} 결제 안내 문자를 ${targets.length}곳에 보냅니다.`,
            `안내 금액 합계 ${won(selectedTotal)}원 · 결제 예정일 ${scheduledOn}`,
            resendCount > 0 ? `⚠️ 이 중 ${resendCount}곳은 이미 보낸 곳입니다(재발송).` : '',
            '',
            '실제 고객에게 문자가 나갑니다. 계속할까요?',
        ].filter((l) => l !== null).join('\n');
        if (!window.confirm(msg)) return;

        stopRef.current = false;
        setSending({ done: 0, total: targets.length });
        for (let i = 0; i < targets.length; i++) {
            if (stopRef.current) break;
            const row = targets[i];
            try {
                const r = (await sendBillingNotice({ settlementId: row.settlementId, ...body(), resend: noticedAlready(row) }))?.data ?? {};
                setResults((p) => ({ ...p, [row.settlementId]: { ok: r.accepted, text: r.accepted ? `접수 ${r.sentAt}` : `실패 ${r.resultCode ?? ''} ${r.resultMessage ?? ''}`.trim() } }));
            } catch (e) {
                setResults((p) => ({ ...p, [row.settlementId]: { ok: false, text: errorText(e) } }));
            }
            setSending({ done: i + 1, total: targets.length });
        }
        setSending(null);
        // 서버 기록(발송 결과·열람)을 다시 읽는다. 결과 표시는 유지한다.
        try {
            const res = (await previewBillingNotice({ month, ...body() }))?.data;
            if (res?.rows) setData(res);
            setSelected(new Set());
        } catch { /* 결과는 이미 화면에 있다 */ }
    };

    const blocked = !data || stale || loading || !!sending || !data.senderConfigured || data.quietHours
        || subjectBytes > SUBJECT_MAX_BYTES;

    return (
        <div className="fixed inset-0 z-[1500] bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl my-6">
                <div className="flex items-center justify-between border-b px-5 py-3">
                    <div>
                        <div className="font-bold text-gray-900">{month} 결제 안내 문자 (LMS)</div>
                        <div className="text-xs text-gray-500">확정된 정산서만 대상입니다. 결제 실행 전에 금액·결제일·결제 회사와 모바일 고지서 링크를 보냅니다.</div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-700 text-xl leading-none" onClick={onClose} disabled={!!sending}>✕</button>
                </div>

                <div className="p-5 space-y-4">
                    {/* 설정 */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="space-y-3">
                            <label className="block text-sm">
                                <span className="text-gray-600">결제 예정일</span>
                                <input type="date" className="mt-1 w-full border rounded px-2 py-1.5" value={scheduledOn}
                                       onChange={(e) => setScheduledOn(e.target.value)} />
                            </label>
                            <div className="text-sm space-y-2">
                                <div className="text-gray-600">문의처 <code className="text-xs">{'{문의처}'}</code> — 브랜드별 카카오톡 채널 채팅</div>
                                {[
                                    ['오모플레이', contactOmoplay, setContactOmoplay, 'omoplay'],
                                    ['단골톡', contactDangoltalk, setContactDangoltalk, 'dangoltalk'],
                                ].map(([label, value, setValue, key]) => (
                                    <label key={key} className="block">
                                        <span className="text-xs text-gray-500">{label}</span>
                                        <input className="mt-0.5 w-full border rounded px-2 py-1.5 text-xs font-mono" value={value}
                                               placeholder="불러오는 중…" onChange={(e) => setValue(e.target.value)} />
                                    </label>
                                ))}
                                <div className="text-xs text-gray-400">
                                    기준값은 system_config <code>billing-notice.contact.omoplay</code>·<code>billing-notice.contact.dangoltalk</code>
                                    (없으면 기본 채팅 링크)입니다. 여기서 바꾼 값은 이번 발송에만 쓰입니다.
                                </div>
                            </div>
                            <label className="block text-sm">
                                <span className="text-gray-600">시험 발송 받을 번호(관리자)</span>
                                <input className="mt-1 w-full border rounded px-2 py-1.5" value={testPhone} placeholder="010-0000-0000"
                                       onChange={(e) => setTestPhone(e.target.value)} />
                            </label>
                            <button className="w-full px-3 py-2 rounded bg-gray-800 text-white text-sm disabled:opacity-50" onClick={load} disabled={loading || !!sending || !scheduledOn}>
                                {loading ? '불러오는 중…' : data ? '미리보기 다시 만들기' : '미리보기'}
                            </button>
                            {stale && <div className="text-xs text-amber-700">설정을 바꿨습니다. 미리보기를 다시 만들어야 보낼 수 있습니다.</div>}
                        </div>
                        <div className="lg:col-span-2 space-y-3">
                            <div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">
                                        제목 <span className={subjectBytes > SUBJECT_MAX_BYTES ? 'text-red-600 font-semibold' : 'text-gray-400'}>({subjectBytes} / {SUBJECT_MAX_BYTES}바이트 · 변수 치환 전)</span>
                                    </span>
                                    <button className="text-xs underline text-gray-500" disabled={!defaults}
                                            onClick={() => { setSubject(defaults?.subject ?? ''); lsSet(LS_SUBJECT, ''); }}>기본 제목으로</button>
                                </div>
                                <input className="mt-1 w-full border rounded px-2 py-1.5 text-sm" value={subject}
                                       placeholder="불러오는 중…"
                                       onChange={(e) => { setSubject(e.target.value); lsSet(LS_SUBJECT, e.target.value); }} />
                                <div className="text-xs text-gray-400">{'{월}'} = 정산서의 이용월(예: 9월)입니다. 결제일이 다음 달이라도 금액은 이용월분이라 기본 제목은 “{'{월}'}분” 으로 씁니다. 실제 길이는 미리보기에서 확인하세요(30바이트를 넘으면 보내지 않습니다).</div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">문구 — 변수 <code className="text-xs">{'{브랜드} {상호} {월} {금액} {결제일} {결제수단} {링크} {문의처}'}</code></span>
                                    <button className="text-xs underline text-gray-500" disabled={!defaults}
                                            onClick={() => { setTemplate(defaults?.template ?? ''); lsSet(LS_TEMPLATE, ''); }}>기본 문구로</button>
                                </div>
                                <textarea
                                    className="mt-1 w-full border rounded px-2 py-1.5 font-mono text-xs h-64"
                                    value={template}
                                    placeholder="불러오는 중…"
                                    onChange={(e) => { setTemplate(e.target.value); lsSet(LS_TEMPLATE, e.target.value); }}
                                />
                                <div className="text-xs text-gray-400">브랜드는 채널 플래그 ‘오모플레이’ 가 있으면 오모플레이, 나머지는 모두 단골톡입니다.</div>
                            </div>
                        </div>
                    </div>

                    {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 whitespace-pre-wrap">{error}</div>}
                    {data && !data.senderConfigured && (
                        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                            발신번호가 설정되지 않아 보낼 수 없습니다. 설정 화면의 system_config 에 <code>billing-notice.sender-phone</code>(SureM 등록 발신번호, 숫자만)를 넣어주세요.
                        </div>
                    )}
                    {data?.quietHours && (
                        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">지금은 야간(21:00~08:00)이라 보낼 수 없습니다.</div>
                    )}

                    {data && (
                        <>
                            <div className="flex items-center flex-wrap gap-3 text-sm">
                                <span className="font-semibold">대상 {rows.length}곳</span>
                                <span className="text-gray-500">선택 {selectedRows.length}곳 · {won(selectedTotal)}원</span>
                                <label className="flex items-center gap-1 text-xs text-gray-600">
                                    <input type="checkbox" checked={hidePaid} onChange={(e) => setHidePaid(e.target.checked)} />
                                    수납 완료 숨기기
                                </label>
                                <button className="text-xs underline text-gray-500" onClick={() => setSelected(new Set(rows.filter((r) => selectable(r) && !noticedAlready(r)).map((r) => r.settlementId)))}>
                                    안 보낸 곳 전체 선택
                                </button>
                                <button className="text-xs underline text-gray-500" onClick={() => setSelected(new Set())}>선택 해제</button>
                                <div className="ml-auto flex items-center gap-2">
                                    {sending && (
                                        <>
                                            <span className="text-xs text-gray-600">보내는 중 {sending.done}/{sending.total}</span>
                                            <button className="px-2 py-1 text-xs border rounded" onClick={() => { stopRef.current = true; }}>중지</button>
                                        </>
                                    )}
                                    <button
                                        className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm disabled:opacity-40"
                                        disabled={blocked || selectedRows.length === 0}
                                        onClick={sendSelected}
                                    >
                                        선택 {selectedRows.length}곳 발송
                                    </button>
                                </div>
                            </div>

                            <div className="border rounded overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-xs text-gray-600">
                                    <tr className="text-left">
                                        <th className="px-2 py-2 w-8"></th>
                                        <th className="px-2 py-2">업체</th>
                                        <th className="px-2 py-2">브랜드</th>
                                        <th className="px-2 py-2 text-right">금액</th>
                                        <th className="px-2 py-2">받는 번호</th>
                                        <th className="px-2 py-2">결제</th>
                                        <th className="px-2 py-2">상태</th>
                                        <th className="px-2 py-2">고지서 열람</th>
                                        <th className="px-2 py-2"></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {rows.map((r) => {
                                        const res = results[r.settlementId];
                                        const done = noticedAlready(r);
                                        return (
                                            <React.Fragment key={r.settlementId}>
                                                <tr className="border-t align-top">
                                                    <td className="px-2 py-2">
                                                        <input type="checkbox" disabled={!selectable(r) || !!sending}
                                                               checked={selected.has(r.settlementId)} onChange={() => toggle(r.settlementId)} />
                                                    </td>
                                                    <td className="px-2 py-2">
                                                        <div className="font-medium">{r.storeName}</div>
                                                        <div className="text-xs text-gray-400">ch {r.channelId}</div>
                                                    </td>
                                                    <td className="px-2 py-2">
                                                        <span className={`text-xs px-1.5 py-0.5 rounded ${r.brand === '단골톡' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}`}>{r.brand}</span>
                                                    </td>
                                                    <td className="px-2 py-2 text-right whitespace-nowrap">{won(r.total)}</td>
                                                    <td className="px-2 py-2 whitespace-nowrap">{r.phoneMasked ?? <span className="text-red-600">없음</span>}</td>
                                                    <td className="px-2 py-2 text-xs">
                                                        {r.billingMethod === 'TAX_INVOICE' ? '세금계산서' : (r.cardLabel ?? <span className="text-red-600">카드 없음</span>)}
                                                        {r.paidAt && <div className="text-emerald-700">수납 {r.paidAt}</div>}
                                                    </td>
                                                    <td className="px-2 py-2 text-xs">
                                                        {r.problems.map((p) => <div key={p} className="text-red-600">{p}</div>)}
                                                        {r.lastNotice && (
                                                            <div className={r.lastNotice.accepted ? 'text-emerald-700' : 'text-amber-700'}>
                                                                {r.lastNotice.kind === 'TEST' ? '시험 ' : ''}{r.lastNotice.accepted ? '보냄' : `결과 ${r.lastNotice.resultCode ?? '확인 불가'}`} {r.lastNotice.sentAt}
                                                            </div>
                                                        )}
                                                        {res && <div className={res.ok ? 'text-emerald-700' : 'text-red-600'}>{res.text}</div>}
                                                        {!r.problems.length && !r.lastNotice && !res && <span className="text-gray-400">보낼 수 있음</span>}
                                                        {done && selected.has(r.settlementId) && <div className="text-amber-700">재발송됩니다</div>}
                                                    </td>
                                                    <td className="px-2 py-2 text-xs whitespace-nowrap">
                                                        {r.linkLocked ? <span className="text-red-600">잠김</span>
                                                            : r.linkViewCount > 0 ? <span className="text-emerald-700">{r.linkViewCount}회 · {r.linkLastViewedAt}</span>
                                                                : <span className="text-gray-400">안 봄</span>}
                                                    </td>
                                                    <td className="px-2 py-2 text-xs whitespace-nowrap space-x-2">
                                                        <button className="underline" onClick={() => setExpanded(expanded === r.settlementId ? null : r.settlementId)}>
                                                            문구 {r.bytes}B
                                                        </button>
                                                        {r.linkUrl && <a className="underline" href={r.linkUrl} target="_blank" rel="noreferrer">고지서</a>}
                                                        <button className="underline disabled:opacity-40" disabled={stale || !!sending || !data.senderConfigured || data.quietHours}
                                                                onClick={() => sendTest(r)}>시험</button>
                                                    </td>
                                                </tr>
                                                {expanded === r.settlementId && (
                                                    <tr className="bg-gray-50">
                                                        <td></td>
                                                        <td colSpan={8} className="px-2 py-2">
                                                            <div className="text-xs text-gray-500 mb-1">제목: {r.subject} · {r.bytes} / 2000 바이트</div>
                                                            <pre className="text-xs whitespace-pre-wrap bg-white border rounded p-3 max-w-xl">{r.text}</pre>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-xs text-gray-400">
                                ‘보냄’ = SureM 접수 성공(A0000)입니다. 실제 수신 여부가 아닙니다. 결과를 알 수 없는 건(호출 실패 등)도 중복을 막기 위해 보낸 것으로 보며, 다시 보내면 ‘재발송’으로 나갑니다.
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
